import compareListQuery from '~layers/app/graphql/compare/compareList.gql'
import createCompareListQuery from '~layers/app/graphql/compare/createCompareList.gql'
import addProductsToCompareListQuery from '~layers/app/graphql/compare/addProductsToCompareList.gql'
import removeProductsFromCompareListQuery from '~layers/app/graphql/compare/removeProductsFromCompareList.gql'

type CompareStorage = {
  url: string
  uid: string
  name: string
}

type CompareProductList = {
  url: string
  name: string
}

export function useCompareProductList() {
  const compareStorage = useLocalStorage<CompareStorage>('compareProductList', [], { initOnMounted: true })

  const rootCategory = useState('rootCategory', () => ref<CompareProductList | null>(null))

  function setRootCategory(meta: CompareProductList) {
    rootCategory.value = meta
  }

  const compareProductList = useState('compareProductList', () => ({} as any))

  const currentList = computed(() =>
    compareStorage.value.find((item: CompareStorage) => item.url === rootCategory.value?.url),
  )

  function getListByUid(uid: string) {
    return compareStorage.value.find((item: CompareStorage) => item.uid === uid)
  }

  function getCompareProductListData(forceRefresh = false) {
    if (currentList.value) {
      // If we already have data and not forcing refresh, skip
      if (!forceRefresh && compareProductList.value?.items?.length > 0) {
        return
      }

      const { onResult } = useQuery(compareListQuery, () => ({
        uid: currentList.value?.uid,
      }), {
        fetchPolicy: forceRefresh ? 'network-only' : 'cache-first',
      })

      onResult((queryResult: any) => {
        if (!queryResult.loading && queryResult?.data.compareList?.item_count) {
          compareProductList.value = queryResult.data.compareList
        }
      })
    }
  }

  function isInCompareList(productId: any) {
    return compareProductList.value?.items?.find((item: any) => item.product.id === productId)
  }

  async function addProductsToCompareList(productId: string) {
    const {
      mutate: addProductsToCompareListMutation,
    } = useMutation(
      addProductsToCompareListQuery,
      { clientId: 'customer' },
    )

    try {
      const result = await addProductsToCompareListMutation({
        input: {
          uid: currentList.value?.uid,
          products: [productId],
        },
      })

      if (result.data?.addProductsToCompareList) {
        compareProductList.value = result.data.addProductsToCompareList
      }
    }
    catch (error: any) {
      console.error('addProductsToCompareListQuery error', error)
    }
  }

  async function removeProductsFromCompareList(productId: string) {
    const {
      mutate: removeProductsFromCompareListMutation,
    } = useMutation(
      removeProductsFromCompareListQuery,
      {
        clientId: 'customer',
        update: (cache, { data }) => {
          if (data?.removeProductsFromCompareList && currentList.value?.uid) {
            cache.writeQuery({
              query: compareListQuery,
              variables: { uid: currentList.value.uid },
              data: {
                compareList: data.removeProductsFromCompareList,
              },
            })
          }
        },
      },
    )

    try {
      const result = await removeProductsFromCompareListMutation({
        input: {
          uid: currentList.value?.uid,
          products: [productId],
        },
      })

      if (result?.data?.removeProductsFromCompareList) {
        compareProductList.value = result.data.removeProductsFromCompareList
      }
    }
    catch (error: any) {
      console.error('removeProductsFromCompareListQuery error', error)
    }
  }

  async function createCompareList(productId: string) {
    const {
      mutate: createCompareListMutation,
    } = useMutation(
      createCompareListQuery,
      { clientId: 'customer' },
    )

    try {
      const result = await createCompareListMutation({
        input: {
          products: [productId],
        },
      })

      const uid = result?.data.createCompareList.uid
      if (uid) {
        compareStorage.value.push({ uid, ...rootCategory.value })
      }

      if (result.data?.createCompareList?.item_count) {
        compareProductList.value = result.data.createCompareList
      }
    }
    catch (error: any) {
      console.error('createCompareListQuery error', error)
    }
  }

  const toggleCompareProductList = async (productId: string) => {
    if (currentList.value) {
      if (isInCompareList(productId)) {
        await removeProductsFromCompareList(productId)
      }
      else {
        await addProductsToCompareList(productId)
      }
    }
    else {
      await createCompareList(productId)
    }
  }

  return {
    compareProductList,
    compareStorage,
    currentList,
    getCompareProductListData,
    isInCompareList,
    removeProductsFromCompareList,
    rootCategory,
    setRootCategory,
    toggleCompareProductList,
    getListByUid,
  }
}
